import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Coins } from 'lucide-react';
import { useCreditStore } from '@/stores/creditStore';
import { useOrgStore } from '@/stores/orgStore';
import { usePaywallStore } from '@/stores/paywallStore';
import { usePaywallCheck } from '@/hooks/usePaywallCheck';

export function CreditsBadge() {
  const activeOrgId = useOrgStore(s => s.activeOrgId);
  const balance = useCreditStore(s => s.balance);
  const fetchBalance = useCreditStore(s => s.fetchBalance);
  const { needsUpgrade, needsTrial, isExhausted, isCancelledTrial, isExhaustedTrial, isExhaustedPaid } = usePaywallCheck();
  const openPaywall = usePaywallStore(s => s.openPaywall);

  React.useEffect(() => {
    if (activeOrgId) fetchBalance();
  }, [activeOrgId]);

  if (!balance) return null;

  // Scenario A: Fresh alpha user — show normal "0 cr" badge, not a paywall badge
  // needsTrial is true but we show a normal credit display for this scenario
  if (needsTrial) {
    const credits = balance.balanceCredits;
    return (
      <Link
        to="/organization/credits"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 hover:opacity-80 transition-opacity whitespace-nowrap"
        title={`Credit balance: ${Math.max(0, credits).toLocaleString()} credits`}
      >
        <Coins className="w-3 h-3" />
        {Math.max(0, credits).toLocaleString()}<span className="hidden sm:inline"> cr</span>
      </Link>
    );
  }

  // Scenario B: Cancelled trial — red pulsing "Trial cancelled"
  if (isCancelledTrial) {
    return (
      <PaywallBadge
        text="Trial cancelled"
        variant="cancelled_trial"
        bgColor="rgba(239,68,68,0.15)"
        bgColorEnd="rgba(239,68,68,0.08)"
        textColor="#dc2626"
        borderColor="rgba(239,68,68,0.2)"
        pulseColor="rgba(239,68,68,0.3)"
        openPaywall={openPaywall}
      />
    );
  }

  // Scenario C: Exhausted trial — warning-yellow "Credits used up"
  if (isExhaustedTrial) {
    return (
      <PaywallBadge
        text="Credits used up"
        variant="exhausted_trial"
        bgColor="rgba(245,158,11,0.15)"
        bgColorEnd="rgba(245,158,11,0.08)"
        textColor="#d97706"
        borderColor="rgba(245,158,11,0.2)"
        pulseColor="rgba(245,158,11,0.3)"
        openPaywall={openPaywall}
      />
    );
  }

  // Scenario D: Paid active, out of credits — red pulsing "Out of credits"
  if (isExhaustedPaid) {
    return (
      <PaywallBadge
        text="Out of credits"
        variant="exhausted"
        bgColor="rgba(239,68,68,0.15)"
        bgColorEnd="rgba(239,68,68,0.08)"
        textColor="#dc2626"
        borderColor="rgba(239,68,68,0.2)"
        pulseColor="rgba(239,68,68,0.3)"
        openPaywall={openPaywall}
      />
    );
  }

  // Scenario E: Expired/cancelled — red pulsing "Upgrade"
  if (needsUpgrade || isExhausted) {
    return (
      <PaywallBadge
        text="Upgrade"
        variant="upgrade"
        bgColor="rgba(239,68,68,0.15)"
        bgColorEnd="rgba(239,68,68,0.08)"
        textColor="#dc2626"
        borderColor="rgba(239,68,68,0.2)"
        pulseColor="rgba(239,68,68,0.3)"
        openPaywall={openPaywall}
      />
    );
  }

  // Normal credit balance display
  const credits = balance.balanceCredits;
  const color =
    credits <= 0    ? 'bg-red-100 text-red-700' :
    credits < 500   ? 'bg-orange-100 text-orange-700' :
                      'bg-green-100 text-green-700';

  return (
    <Link
      to="/organization/credits"
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color} hover:opacity-80 transition-opacity whitespace-nowrap`}
      title={`Credit balance: ${Math.max(0, credits).toLocaleString()} credits`}
    >
      <Coins className="w-3 h-3" />
      {Math.max(0, credits).toLocaleString()}<span className="hidden sm:inline"> cr</span>
    </Link>
  );
}

/* ─── Paywall Badge Button ──────────────────── */
function PaywallBadge({ text, variant, bgColor, bgColorEnd, textColor, borderColor, pulseColor, openPaywall }: {
  text: string;
  variant: Parameters<typeof openPaywall>[1];
  bgColor: string;
  bgColorEnd: string;
  textColor: string;
  borderColor: string;
  pulseColor: string;
  openPaywall: (source: string, variant?: any) => void;
}) {
  const animId = `creditsBadgePulse_${variant}`;
  return (
    <button
      onClick={() => openPaywall('badge', variant)}
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
      style={{
        background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColorEnd} 100%)`,
        color: textColor,
        border: `1px solid ${borderColor}`,
        animation: `${animId} 2s ease-in-out infinite`,
      }}
      title={`${text} — click to view options`}
    >
      <Lock className="w-3 h-3" />
      {text}
      <style>{`
        @keyframes ${animId} {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 ${pulseColor}; }
          50% { opacity: 0.85; box-shadow: 0 0 0 4px transparent; }
        }
      `}</style>
    </button>
  );
}
